import { ArrowHeadRightIcon, Button, LogOutIcon, Table, TableColumns } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { AccessOverview } from 'components/AccessOverview/AccessOverview';
import { BpCard } from 'components/BpCard/BpCard';
import { ChildrenOverview } from 'components/Children/components/ChildrenOverview/ChildrenOverview';
import { useConfirm } from 'hooks/useConfirm';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useQuery } from 'urql';
import { AccountForm } from '../../components/AccountForm/AccountForm';
import { BpHeader } from '../../components/BpHeader/BpHeader';
import { BpPage } from '../../components/BpPage/BpPage';
import { NotificationSettingsForm } from '../../components/NotificationSettingsForm/NotificationSettingsForm';
import { ProfileForm } from '../../components/ProfileForm/ProfileForm';
import { useAuthClaims } from '../../hooks/useAuthClaims';

const ProfilePageQueryDocument = graphql(`
  query ProfilePageData($profilesWhere: ProfileWhere!, $profilesOptions: ProfileOptions!) {
    ...ProfileFormFields
    ...AccessOverviewFormFields
    ...NotificationSettingsFormFields
  }
`);

export type CombinedProfile = {
  email?: string | null;
  loginEmail?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  threemaId?: string | null;
  workPass?: string | null;
  workUser?: string | null;
};

export const ProfilePage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const childProfilesClaim = pimAuthClaims.getChildProfiles();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const auth = useAuth();

  const { ConfirmationDialog: ConfirmImpersonateDialog, confirm: confirmImpersonate } = useConfirm({
    defaultTitle: t('children.impersonateProfile'),
    defaultMessage: t('children.impersonateConfirm'),
    defaultConfirmText: t('children.impersonate'),
  });

  const [editProfile, setEditProfile] = useState<boolean>(false);
  const [editAccount, setEditAccount] = useState<boolean>(false);

  const [{ data }] = useQuery({
    query: ProfilePageQueryDocument,
    variables: {
      profilesWhere: {
        uuid: profileUuid,
      },
      profilesOptions: {
        limit: 1,
      },
    },
  });

  async function handleImpersonate(childUuid: string) {
    const confirm = await confirmImpersonate();
    if (!confirm) return;
    auth.signinRedirect({
      prompt: 'select_account consent',
      login_hint: childUuid,
    });
  }

  const organizations = useMemo(
    () =>
      pimAuthClaims
        .getOtherProfiles()
        .filter((profile) => profile.organization.name !== '__omni__')
        .map((profile) => ({
          uuid: profile.uuid,
          name: profile.organization.name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [pimAuthClaims],
  );

  const columns: TableColumns<{ uuid: string; name: string }>[] = [
    {
      id: 'name',
      header: t('organizations.title', { count: 1 }),
      accessorKey: 'name',
      size: 300,
      canExpand: true,
    },
  ];

  return (
    <BpPage
      hideHeader
      title={t('profiles.titleSingular')}
      isForbidden={false}
      organization={pimAuthClaims.getOrganization()}
    >
      <BpHeader
        size='l'
        headline={pimAuthClaims.getProfile().displayName ?? ''}
        actions={[
          {
            icon: <LogOutIcon />,
            hierarchy: 'primary',
            text: t('common.logout'),
            callback: () => auth.signoutRedirect(),
          },
        ]}
      />
      <BpCard
        alwaysCollapsible
        isCollapsed={!editProfile}
        onCollapse={() => setEditProfile(true)}
        header={{
          headline: t('profiles.editProfile'),
        }}
        className='mb-6'
      >
        <ProfileForm query={data} onClose={() => setEditProfile(false)} />
      </BpCard>

      {!pimAuthClaims.getUser().impersonateOnly && (
        <BpCard
          alwaysCollapsible
          isCollapsed={!editAccount}
          onCollapse={() => setEditAccount(true)}
          header={{
            headline: t('profiles.editAccount'),
          }}
          className='mb-6'
        >
          <AccountForm />
        </BpCard>
      )}

      <BpCard
        isCollapsed={true}
        alwaysCollapsible
        header={{
          headline: t('notifications.title'),
        }}
        className='mb-6'
      >
        <NotificationSettingsForm query={data} />
      </BpCard>

      {childProfilesClaim.length > 0 && (
        <BpCard
          alwaysCollapsible
          isCollapsed
          header={{
            headline: t('children.myChildren'),
          }}
          className='mb-6'
        >
          <ChildrenOverview onProfileSwitch={(uuid) => handleImpersonate(uuid)} />
        </BpCard>
      )}

      <BpCard header={{ headline: t('common.accounts') }} alwaysCollapsible isCollapsed className='mb-6'>
        <AccessOverview query={data} />
      </BpCard>

      {pimAuthClaims.getUser().isOmniAdmin && (
        <BpCard
          header={{
            headline: t('auth.support'),
          }}
        >
          <Table
            columns={columns}
            data={organizations}
            lastColWidth={'120px'}
            showActionBar
            lastCol={(row) => {
              return (
                <>
                  <Button
                    hierarchy={'secondary'}
                    icon={<ArrowHeadRightIcon />}
                    iconPosition={'right'}
                    onClick={async () => {
                      await auth.signinRedirect({
                        prompt: 'select_account consent',
                        login_hint: row.original.uuid,
                      });
                    }}
                  >
                    Wechseln
                  </Button>
                </>
              );
            }}
          />
        </BpCard>
      )}

      <ConfirmImpersonateDialog />
    </BpPage>
  );
};
